/**
 * Contrats de jeu POP! — la couture entre le serveur et le frontend.
 *
 * RÈGLE : aucune forme de donnée de jeu ne se redéclare ailleurs. Le serveur
 * valide les entrées clientes avec ces schémas, et le frontend en dérive ses
 * types. Si une forme change, elle change ICI, et TypeScript signale tout ce
 * qui casse des deux côtés.
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────── identité de partie
/**
 * Code de salle : 6 caractères, sans `I`, `O`, `0` ni `1`.
 *
 * Ce n'est pas de l'esthétique. Un code POP! se lit à voix haute dans une pièce
 * bruyante et se recopie sur un téléphone : `I`/`1` et `O`/`0` sont la première
 * source d'échec de connexion, et l'utilisateur ne comprend pas pourquoi « ça
 * ne marche pas ».
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;
export const roomCodeSchema = z
    .string()
    .length(ROOM_CODE_LENGTH)
    .regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]+$`), 'code de salle invalide');
/** Pseudo : court, affichable, sans espace superflu. */
export const pseudoSchema = z
    .string()
    .trim()
    .min(1, 'le pseudo ne peut pas être vide')
    .max(20, 'le pseudo est limité à 20 caractères');
// ─────────────────────────────────────────────────────────────── état de partie
export const playerSchema = z.object({
    id: z.string().uuid(),
    pseudo: pseudoSchema,
    score: z.number().int().nonnegative(),
    /** Un joueur déconnecté reste dans la partie : il peut revenir. */
    connected: z.boolean(),
});
export const roomStatusSchema = z.enum(['lobby', 'playing', 'finished']);
/**
 * Manche en cours.
 *
 * `deadlineAt` est une DATE ABSOLUE (ISO 8601), pas une durée. C'est le serveur
 * qui décide quand la manche expire ; envoyer « 30 secondes » obligerait chaque
 * client à calculer sa propre échéance à partir de son horloge, et deux
 * téléphones en désaccord produiraient deux fins de manche différentes.
 */
export const roundSchema = z.object({
    index: z.number().int().nonnegative(),
    total: z.number().int().positive(),
    activePlayerId: z.string().uuid(),
    prompt: z.string().min(1),
    deadlineAt: z.string().datetime(),
});
export const roomStateSchema = z.object({
    code: roomCodeSchema,
    status: roomStatusSchema,
    /** L'hôte est le seul à pouvoir lancer la partie. */
    hostId: z.string().uuid(),
    players: z.array(playerSchema),
    round: roundSchema.nullable(),
});
// ────────────────────────────────────────────────────────── requêtes HTTP
export const createRoomRequestSchema = z.object({ pseudo: pseudoSchema });
export const createRoomResponseSchema = z.object({
    code: roomCodeSchema,
    playerId: z.string().uuid(),
    state: roomStateSchema,
});
export const joinRoomRequestSchema = z.object({ pseudo: pseudoSchema });
export const joinRoomResponseSchema = z.object({
    playerId: z.string().uuid(),
    state: roomStateSchema,
});
export const submitAnswerRequestSchema = z.object({
    text: z.string().trim().min(1).max(120),
});
// ──────────────────────────────────────────────── événements temps réel
/**
 * Noms d'événements. Centralisés et typés : une faute de frappe côté client
 * produirait sinon un abonnement silencieusement mort — le pire des bugs temps
 * réel, puisque rien n'échoue et rien n'arrive.
 */
export const CLIENT_EVENT = {
    /** Le client demande l'état courant (reconnexion, rechargement). */
    Sync: 'room:sync',
    /** L'hôte lance la partie. */
    Start: 'game:start',
    /** Le joueur actif soumet sa réponse. */
    Answer: 'turn:answer',
};
export const SERVER_EVENT = {
    /** Nouvel état complet. Le client REMPLACE son état, il ne le patche pas. */
    State: 'room:state',
    /** Une manche commence. */
    RoundStarted: 'round:started',
    /** Une manche est résolue, avec son verdict. */
    RoundResolved: 'round:resolved',
    /** La partie est terminée. */
    Finished: 'game:finished',
    /** Erreur d'action, à afficher à l'utilisateur. */
    Rejected: 'action:rejected',
};
/** Verdict d'une manche — le serveur SEUL le décide. */
export const roundResolvedSchema = z.object({
    index: z.number().int().nonnegative(),
    activePlayerId: z.string().uuid(),
    answer: z.string().nullable(),
    accepted: z.boolean(),
    reason: z.enum(['answered', 'timeout']),
});
export const finishedSchema = z.object({
    players: z.array(playerSchema),
    winnerIds: z.array(z.string().uuid()),
});
/** Rejet d'une action : message utilisable, jamais une trace technique. */
export const rejectedSchema = z.object({
    action: z.string(),
    code: z.enum([
        'ROOM_NOT_FOUND',
        'ROOM_FULL',
        'ROOM_STARTED',
        'NOT_HOST',
        'NOT_ACTIVE_PLAYER',
        'ROUND_EXPIRED',
        'TOO_MANY_PLAYERS',
    ]),
    message: z.string(),
});
/** Limite de joueurs : au-delà, une soirée n'est plus une soirée. */
export const MAX_PLAYERS = 12;
/** Nombre de manches d'une partie. */
export const ROUNDS_PER_GAME = 3;
/** Durée d'une manche, en millisecondes. */
export const ROUND_DURATION_MS = 30_000;
//# sourceMappingURL=game.js.map